import { Card, Col, Form, Input, Row, Select } from "antd";
import { postData } from "components/BOM/AddOrUpdateStyle";
import { debounce } from "lodash";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  PACKING_REPORT_TEMPLATE_PRE_REQUISITES,
  PACKING_REPORT_TEMPLATE_STORE,
} from "../../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../../apiServices/common";
import { alertPop } from "../../../../apiServices/common/helper";
import { useDispatch } from "react-redux";
import { onGetPackingReportTemplatePreData } from "redux/actions";
import { useSelector } from "react-redux";

const { Option } = Select;

const AddPackingReportTemplateForm = forwardRef((props, ref) => {
  const { view, form, id, viewData } = props;

  const buyers = useSelector((state) => state.packingReportTemplate.buyers);
  const isApiDataLoading = useSelector((state) => state.packingReportTemplate.loading);

  const error = useSelector((state) => state.packingReportTemplate.error);
  

  const [loading, setLoading] = useState(false);
  const [buyingOffices, setBuyingOffices] = useState([]);
  const [salesContracts, setSalesContracts] = useState([]);

  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    submit() {
      form
        .validateFields()
        .then(async (values) => {
          setLoading(true);

          const bodyData = {
            ...values,
          };

          try {
            let response = await postData(PACKING_REPORT_TEMPLATE_STORE, bodyData);

            if (response && response?.code === 200) {
              alertPop("success", "Packing Report Template created successfully");

              navigate(`/ex-factory/packing-report-template/edit/${response.data.id}`);
            }
          } catch (error) {
            alertPop("error", error);
          } finally {
            setLoading(false);
          }
        })
        .catch((errorInfo) => {
          alertPop("error", "Please check the form fields and try again.");
        });
    },
    discart() {
      form.resetFields();
    },
  }));

  const handleBuyerChange = useCallback(
    (buyerId, reset = true) => {
      if (reset) {
        form.setFieldsValue({
          buying_office_id: undefined,
          sales_contract_id: undefined,
        });
      }

      const selectedBuyer = buyers.find((buyer) => buyer.id === buyerId);
      if (selectedBuyer) {
        const offices = selectedBuyer.buying_offices.map((office) => office.buying_office);
        setBuyingOffices(offices);
      } else {
        setBuyingOffices([]);
      }

      setSalesContracts([]);
    },
    [buyers, form],
  );

  const handleBuyingOfficeChange = useCallback(
    (officeId, reset = true) => {
      if (reset) {
        form.setFieldsValue({
          sales_contract_id: undefined,
        });
      }

      const buyerId = form.getFieldValue("buyer_id");
      if (!buyerId || !officeId) {
        setSalesContracts([]);
        return;
      }

      const selectedBuyer = buyers.find((buyer) => buyer.id === buyerId);
      if (selectedBuyer) {
        const buyingOfficeData = selectedBuyer.buying_offices.find((office) => office.buying_office.id === officeId);

        if (buyingOfficeData && buyingOfficeData.buying_office.sales_contract_info) {
          setSalesContracts(buyingOfficeData.buying_office.sales_contract_info);
        } else {
          setSalesContracts([]);
        }
      }
    },
    [buyers, form],
  );

  
  
  
  

  const dispatch = useDispatch();

  useEffect(() => {
    
    dispatch(onGetPackingReportTemplatePreData());
  }, [dispatch]);

  useEffect(() => {
    if (id && viewData) {
      form.setFieldsValue({
        name: viewData.name,
        buyer_id: viewData.buyer?.id,
        buying_office_id: viewData.buying_office?.id,
        sales_contract_id: viewData.sales_contract?.id,
        origin: viewData.origin,
      });

      handleBuyerChange(viewData.buyer?.id, false);
      handleBuyingOfficeChange(viewData.buying_office?.id, false);
    }
  }, [form, id, viewData, handleBuyerChange, handleBuyingOfficeChange]);

  return (
    <div className="containt-body">
      <Form layout="vertical" form={form} name="control-hooks">
        <Card>
          <Row gutter={6}>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Template Name"
                name="name"
                rules={[
                  {
                    required: true,
                    message: "Template Name is required",
                  },
                ]}
              >
                <Input className="w-100" disabled={view || isApiDataLoading} size="small" />
              </Form.Item>
            </Col>
            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Buyer"
                name="buyer_id"
                rules={[
                  {
                    required: true,
                    message: "Buyer is required",
                  },
                ]}
              >
                <Select
                  placeholder="Buyer"
                  showSearch
                  style={{ width: "100%" }}
                  onChange={handleBuyerChange}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  disabled={view}
                  allowClear
                  size="small"
                >
                  {buyers?.map((buyer) => (
                    <Option key={buyer.id} value={buyer.id}>
                      {buyer.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Buying Office"
                name="buying_office_id"
                rules={[
                  {
                    required: true,
                    message: "Buying Office is required",
                  },
                ]}
              >
                <Select
                  placeholder="Buying Office"
                  showSearch
                  style={{ width: "100%" }}
                  onChange={handleBuyingOfficeChange}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  disabled={view || !form.getFieldValue("buyer_id")}
                  allowClear
                  size="small"
                >
                  {buyingOffices?.map((office) => (
                    <Option key={office.id} value={office.id}>
                      {office.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Sales Contract"
                name="sales_contract_id"
                rules={[
                  {
                    required: true,
                    message: "Sales Contract is required",
                  },
                ]}
              >
                <Select
                  placeholder="Sales Contract"
                  showSearch
                  style={{ width: "100%" }}
                  filterOption={(input, option) => option.children.toLowerCase().includes(input.toLowerCase())}
                  disabled={view || !form.getFieldValue("buying_office_id")}
                  allowClear
                  size="small"
                >
                  {salesContracts?.map((contract) => (
                    <Option key={contract.id} value={contract.id}>
                      {contract.reference_no}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col className="gutter-row" span={4}>
              <Form.Item
                label="Origin"
                name="origin"
                rules={[
                  {
                    required: false,
                    message: "Origin is required",
                  },
                ]}
              >
                <Input className="w-100" disabled={view || isApiDataLoading} size="small" />
              </Form.Item>
            </Col>
          </Row>
        </Card>
      </Form>
    </div>
  );
});

export default AddPackingReportTemplateForm;
